import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useProductsAgGridColumns, useProductsMain } from '../../hooks';
import { ProductsModel } from '../../models/Products';
import { CardList, ProductsAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { CreateProductButton, FiltersSection, ExternalExcludedProductsButton } from '../interaction';
import { ReservedModal, ReservedModalRef } from '../modals/ReservedModal';
import { StockModal, StockModalRef } from '../modals/StockModal';
import { BulkUploadModal, BulkUploadModalRef } from '../modals/BulkUploadModal';
import {
  ExternalExcludedProductsModal,
  ExternalExcludedProductsModalRef,
} from '../modals/ExternalExcludedProductsModal';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';

export const Main = () => {
  const { t } = useTranslation('productsModule');
  const { toast } = useToast();

  const {
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    productsViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns,
    filterConditions,
    searchTerm,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
  } = useProductsMain();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isBulkUploading, setIsBulkUploading] = useState(false);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const stockModalRef = useRef<StockModalRef>(null);
  const reservedModalRef = useRef<ReservedModalRef>(null);
  const bulkUploadModalRef = useRef<BulkUploadModalRef>(null);
  const externalExcludedModalRef = useRef<ExternalExcludedProductsModalRef>(null);

  const productsApi = useMemo(() => new ProductsApiAdapter(createHttpClient(apiClientWH)), []);

  const handleOpenBulkUpload = useCallback(() => {
    bulkUploadModalRef.current?.open();
  }, []);

  const handleOpenExternalExcluded = useCallback(() => {
    externalExcludedModalRef.current?.openModal();
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    window.open('https://img.go-warehouse.com/massive_load_products_template_locations_2.xlsx', '_blank', 'noopener');
  }, []);

  const handleUploadProducts = useCallback(
    async (file: File) => {
      try {
        setIsBulkUploading(true);
        const response = await productsApi.uploadProductsBulk(file);

        // Verificar si hay errores en la respuesta (aunque sea 200 OK)
        const errorCount = response?.error_count ?? 0;
        const successCount = response?.success_count ?? 0;
        const errorFileUrl = response?.error_file;

        if (errorCount > 0) {
          // Hay errores: mantener el modal abierto y mostrar información de errores
          // El modal se encargará de mostrar el mensaje con el enlace
          return {
            hasErrors: true,
            errorCount,
            successCount,
            errorFileUrl,
            message: response?.message,
          };
        }

        // Todo salió bien: mostrar toast de éxito y cerrar modal
        toast({
          title: t('toasts.bulk_upload_success'),
          description: response?.message || t('toasts.bulk_upload_success'),
          variant: 'default',
        });
        bulkUploadModalRef.current?.close();
        refetch();
        return { hasErrors: false };
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || t('toasts.bulk_upload_error');

        toast({
          title: t('toasts.error_title'),
          description: message,
          variant: 'destructive',
        });
        return { hasErrors: false };
      } finally {
        setIsBulkUploading(false);
      }
    },
    [productsApi, refetch, t, toast],
  );

  const handleStockClick = useCallback((product: ProductsModel) => {
    const stock = product.stock || {};
    stockModalRef.current?.open({
      name: product.name,
      code: product.code,
      image: product.image_hash,
      stock: {
        disponible: stock.disponible ?? 0,
        reservado: stock.reservado ?? 0,
        total: (stock.disponible ?? 0) + (stock.entrante ?? 0) + (stock.reservado ?? 0),
      },
    });
  }, []);

  const handleReservedClick = useCallback((product: ProductsModel) => {
    reservedModalRef.current?.open({
      id: product.id,
      name: product.name,
      code: product.code,
      image: product.image_hash,
      reservedCount: product.stock?.reservado ?? 0,
    });
  }, []);

  // Usuarios 3PL siempre ven filtro y columna de cliente (igual que en Pedidos).
  // No derivar de la respuesta actual: al filtrar por team_id la lista puede no traer `team`.
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const { filteredColumnDefs, tanStackColumns } = useProductsAgGridColumns({
    hasTeamData,
    selectedColumns,
    onStockClick: handleStockClick,
    onReservedClick: handleReservedClick,
    refetch,
  });

  const selectorColumns = useMemo(() => {
    if (hasTeamData) {
      return tanStackColumns;
    }

    return tanStackColumns.filter((column) => {
      const columnId = (column as any).id || (column as any).accessorKey;
      return columnId !== 'client';
    });
  }, [tanStackColumns, hasTeamData]);

  const renderProducts = () => {
    if (isMobile) {
      return (
        <CardList
          {...productsViewProps}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onReservedClick={handleReservedClick}
          onStockClick={handleStockClick}
          selectedColumns={selectedColumns}
        />
      );
    }

    return (
      <ProductsAgGridTable
        products={productsViewProps.products}
        columnDefs={filteredColumnDefs}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando productos...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              allColumns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={fixedColumns}
              exportColumns={exportColumns}
              hasTeamData={hasTeamData}
              searchTerm={searchTerm}
              filterConditions={filterConditions}
            />
          </div>
          <div>
            <TotalResults total={total} />
          </div>
          <div className="shrink-0 absolute right-4 top-2 flex items-center gap-2">
            {/* External Excluded Products Button - Botón de productos externos excluidos */}
            <ExternalExcludedProductsButton onOpenModal={handleOpenExternalExcluded} />
            {total > 0 && <CreateProductButton onOpenBulkUpload={handleOpenBulkUpload} />}
          </div>
        </div>
      </div>

      {/* 🎯 SOLUCIÓN: Siempre renderizar AgGrid, pero con opacity 0 cuando no hay datos */}
      {!isInitialLoad && (
        <div className="relative mb-4 mt-4">
          {/* AgGrid siempre renderizado, con opacity 0 si no hay datos */}
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : total > 0
                  ? 'opacity-100'
                  : 'opacity-0'
            }`}
          >
            {renderProducts()}
          </div>

          {/* EmptyView solo cuando no hay datos */}
          {total === 0 && (
            <div className="absolute inset-0">
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.PRODUCTS}
                customAction={
                  hasActiveFilters ? null : (
                    <CreateProductButton inline={true} onOpenBulkUpload={handleOpenBulkUpload} />
                  )
                }
              />
            </div>
          )}
        </div>
      )}

      <StockModal ref={stockModalRef} />
      <ReservedModal ref={reservedModalRef} />
      <BulkUploadModal
        ref={bulkUploadModalRef}
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUploadProducts}
        isUploading={isBulkUploading}
      />
      <ExternalExcludedProductsModal ref={externalExcludedModalRef} />
    </div>
  );
};
